/**
 * Bridges an `AbortSignal` to an axios `CancelToken` so an in-flight HTTP
 * request is actually aborted when the signal fires.
 *
 * Today, the axios version shipped with `@wix/http-client` (0.21.x) does not
 * honor `signal` on its own, so the `CancelToken` bridge is required for true
 * cancellation. To stay forward-compatible with a future axios upgrade that
 * removes the legacy `CancelToken` API, the bridge is feature-detected: if
 * `httpClient.CancelToken` is unavailable, only `signal` is forwarded (which a
 * modern axios honors natively).
 *
 * `httpClient` is mandatory — passing a `signal` without an `httpClient`
 * would silently produce an uncancellable request under axios 0.21.x, which we
 * want the type checker to prevent. Once `@wix/http-client` ships native
 * `AbortSignal` support, this argument becomes unused at runtime and the helper
 * collapses to attaching `signal` only.
 *
 * `signal` may be `undefined`, in which case the request is returned unchanged.
 */ export const withCancelToken = (request, signal, httpClient)=>{
    if (!signal) {
        return request;
    }
    const cancelToken = supportsCancelToken(httpClient) ? bridgeSignalToCancelToken(signal, httpClient) : undefined;
    return (context)=>({
            ...typeof request === 'function' ? request(context) : request,
            signal,
            ...cancelToken ? {
                cancelToken
            } : null
        });
};
const supportsCancelToken = (httpClient)=>{
    const tokenApi = httpClient.CancelToken;
    return typeof tokenApi?.source === 'function';
};
const bridgeSignalToCancelToken = (signal, httpClient)=>{
    const source = httpClient.CancelToken.source();
    if (signal.aborted) {
        source.cancel('Aborted');
    } else {
        signal.addEventListener('abort', ()=>source.cancel('Aborted'), {
            once: true
        });
    }
    return source.token;
};

//# sourceMappingURL=with-cancel-token.js.map